// AutoTranslate: walks the live DOM after every route change and translates
// any remaining hardcoded Czech text via /api/translate-batch.
//
// Design goals:
//  1. Each translation is fetched at most ONCE per (source, lang) pair — cached
//     in localStorage forever (not sessionStorage) so it persists across visits.
//  2. Each text node keeps a memo of its last-translated source; if the memo
//     still matches the current node value, we skip it entirely. This prevents
//     the "translated → observer fires → looks like new Czech text → re-translate
//     → flicker/change" loop the user reported.
//  3. Requests are batched aggressively but capped at 80 strings/req to avoid
//     Google truncation issues.
//  4. Failures do NOT poison the cache — we only cache real translations so the
//     next scan retries missed strings.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";

const HAS_LETTERS = /[a-zA-ZáčďéěíňóřšťúůýžÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ]/;
const MEANINGFUL = /[a-zA-ZáčďéěíňóřšťúůýžÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ]{2,}/;
const SKIP_CONTAINERS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "INPUT",
]);

// Per-text-node memo: last translation we applied. If the current node value
// still equals `translated`, we won't touch it again.
//   node → { source: <original CZ>, translated: <current SK/EN/HU> }
const NODE_MEMO = new WeakMap();

function shouldTranslate(node, trimmed) {
  if (!trimmed || trimmed.length < 2 || trimmed.length > 800) return false;
  if (!MEANINGFUL.test(trimmed)) return false;
  if (!HAS_LETTERS.test(trimmed)) return false;
  // Walk up: skip inside inputs / editable / admin / not-translate scope
  let el = node.parentElement;
  while (el) {
    if (SKIP_CONTAINERS.has(el.tagName)) return false;
    if (el.getAttribute) {
      if (el.getAttribute("contenteditable") === "true") return false;
      if (el.dataset && el.dataset.notranslate === "1") return false;
    }
    el = el.parentElement;
  }
  return true;
}

function cacheKey(lang) {
  return `ss_uitr_${lang}_v2`;
}
function getCache(lang) {
  try {
    return JSON.parse(localStorage.getItem(cacheKey(lang)) || "{}");
  } catch (e) { return {}; }
}
function saveCache(lang, obj) {
  try { localStorage.setItem(cacheKey(lang), JSON.stringify(obj)); }
  catch (e) { /* quota */ }
}

// Break a big array into fixed-size batches.
function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// Serialize scans so overlapping observer fires don't race.
let _running = false;
let _pending = false;

async function runOnce(lang) {
  if (lang === "cs") return;
  if (_running) { _pending = true; return; }
  _running = true;
  try {
    await doScan(lang);
  } finally {
    _running = false;
    if (_pending) {
      _pending = false;
      // small yield so we don't tight-loop the CPU
      setTimeout(() => runOnce(lang), 100);
    }
  }
}

async function doScan(lang) {
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  const candidates = [];
  let n;
  while ((n = walker.nextNode())) {
    const raw = n.nodeValue;
    if (!raw) continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    // Fast path: node already translated & value hasn't changed since last apply
    const memo = NODE_MEMO.get(n);
    if (memo && memo.translated === trimmed) continue;
    // Node was translated but React or other code reset it back to source — retranslate.
    if (!shouldTranslate(n, trimmed)) continue;
    candidates.push({ node: n, original: trimmed, raw });
  }
  if (!candidates.length) return;

  const cache = getCache(lang);

  // Apply cached translations first (synchronous, cheap)
  const toFetch = [];
  for (const c of candidates) {
    const cached = cache[c.original];
    if (typeof cached === "string" && cached && cached !== c.original) {
      if (c.node.nodeValue && c.node.nodeValue.includes(c.original)) {
        c.node.nodeValue = c.node.nodeValue.replace(c.original, cached);
        NODE_MEMO.set(c.node, { source: c.original, translated: cached });
      }
    } else if (cached === undefined) {
      toFetch.push(c);
    }
    // If cached === "" or cached === c.original, we simply skip (no translation needed).
  }
  if (!toFetch.length) return;

  // Dedupe by original
  const uniqSet = new Set();
  const uniq = [];
  for (const c of toFetch) {
    if (!uniqSet.has(c.original)) {
      uniqSet.add(c.original);
      uniq.push(c.original);
    }
  }

  // Fetch in chunks of 80 to stay under Google's practical batch limit
  const translationMap = {};
  for (const batch of chunk(uniq, 80)) {
    try {
      const res = await api.post("/translate-batch", { texts: batch, lang });
      const arr = res.data?.translations || [];
      batch.forEach((src, i) => {
        const t = typeof arr[i] === "string" ? arr[i] : "";
        // Only cache REAL translations (non-empty and different from source, or explicit noop).
        // If backend returned same-as-source, we cache it as noop so we don't retry forever.
        if (t) translationMap[src] = t;
      });
    } catch (e) {
      // network / server error — do NOT cache anything from this batch;
      // next scan will retry these strings.
    }
  }

  // Persist to cache
  let dirty = false;
  for (const [src, t] of Object.entries(translationMap)) {
    if (cache[src] !== t) { cache[src] = t; dirty = true; }
  }
  if (dirty) saveCache(lang, cache);

  // Apply to nodes
  for (const c of toFetch) {
    const t = translationMap[c.original] ?? cache[c.original];
    if (!t || t === c.original) continue;
    if (c.node.nodeValue && c.node.nodeValue.includes(c.original)) {
      c.node.nodeValue = c.node.nodeValue.replace(c.original, t);
      NODE_MEMO.set(c.node, { source: c.original, translated: t });
    }
  }
}

export default function AutoTranslate() {
  const { lang } = useI18n();
  const location = useLocation();

  useEffect(() => {
    if (lang === "cs") return;
    let scheduled = null;
    const schedule = (delay) => {
      if (scheduled) clearTimeout(scheduled);
      scheduled = setTimeout(() => runOnce(lang), delay);
    };
    schedule(250);
    const observer = new MutationObserver(() => schedule(400));
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    return () => {
      observer.disconnect();
      if (scheduled) clearTimeout(scheduled);
    };
  }, [lang, location.pathname]);

  return null;
}
